<template lang="pug">
v-container.pa-8.neutral200(fluid style="height: 90vh")
  .mb-4
    .d-flex.align-center.mb-4
      v-btn.mr-4(icon small)
        v-icon(color="primary" @click="onClickBack") mdi-arrow-left
      .text-2xl.font-semibold.neutral700--text {{ $t('drug_label') }}
    .text-lg.font-medium.neutral700--text {{ $t('drug_label_settings') }}
    .text-sm.font-normal.neutral700--text {{ $t('drug_label_description') }}
  v-row(no-gutters)
    v-col
      template(v-if="isFetching")
        v-skeleton-loader.px-4(type="list-item-three-line@5")
      template(v-else)
        v-card.white.pa-6.border-outline.round-2xl.mb-4(outlined)
          .text-md.font-semibold.neutral700--text.mb-4 {{ $t('properties') }}
          template(v-for="item in propertyItems")
            .d-flex
              y-checkbox.mb-1.shrink.mr-4(v-model="item.value" :disabled="item.default")
                template(v-slot:label)
                  .text-md.ml-1 {{ item.text }}
              y-chip.chip(v-if="item.default" :label="$t('required_by_system')" size="xs" chipClass="neutral500--text" outlined)
    v-col.pl-8
      template(v-if="isFetching")
        v-skeleton-loader.px-4(type="list-item-three-line@5")
      template(v-else)
        preview(:style="height")
          preview-item(:title="$t('drug_label_preview')")
            drug-label-preview.mt-4(:phone="phone.value" :address="address.value" :quantity="quantity.value" :precaution="precaution.value" :date="date.value" :expiryDate="expiryDate.value" :duration="duration.value" :indication="indication.value")
  v-footer.white(v-if="!isFetching" padless fixed style="z-index: 20;")
    .d-flex.flex-column.grow.mb-2
      v-divider
      .d-flex.justify-end.align-center.mt-2.mx-2
        v-btn.button-md.underline.mr-2(text color="primary" @click="onClickBack") {{ $t('cancel') }}
        v-btn.button-md(depressed color="primary" :loading="isLoading" @click="onSave") {{ $t('save') }}
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Preview from '@/components/clinic/settings/Preview.vue'
import PreviewItem from '@/components/clinic/settings/PreviewItem.vue'
import DrugLabelPreview from '@/components/clinic/document/preview/DrugLabelPreview.vue'

export default {
  components: {
    Preview, PreviewItem, DrugLabelPreview
  },
  data() {
    return {
      isFetching: true,
      isLoading: false,
      propertyItems: [
        {
          text: 'Clinic name', 
          value: true,
          default: true
        },
        {
          text: 'Medication', 
          value: true,
          default: true
        },
        {
          text: 'Patient details', 
          value: true,
          default: true
        },
        {
          text: 'Instruction', 
          value: true,
          default: true
        },
        {
          text: 'Address', 
          value: false
        },
        {
          text: 'Tel number', 
          value: false
        },
        {
          text: 'Precaution', 
          value: false
        },
        {
          text: 'Quantity', 
          value: false
        },
        {
          text: 'Date', 
          value: false
        },
        {
          text: 'Expiry date', 
          value: false
        },
        {
          text: 'Duration', 
          value: false
        },
        {
          text: 'Indication', 
          value: false
        },
      ],

    }
  },
  mounted () {
    this.fetchDrugLabel()
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    address () {
      return this.propertyItems[4]
    },
    phone () {
      return this.propertyItems[5]
    },
    quantity () {
      return this.propertyItems[7]
    },
    precaution () {
      return this.propertyItems[6]
    },
    date () {
      return this.propertyItems[8]
    },
    expiryDate () {
      return this.propertyItems[9]
    },
    duration () {
      return this.propertyItems[10]
    },
    indication () {
      return this.propertyItems[11]
    },
    height () {
      let value = '70vh'
      if (!this.$vuetify.breakpoint.xl && (this.address.value || this.date.value || this.expiryDate.value)) {
        value = '80vh'
      }
      return `min-height:${value}`
    },
    payload () {
      return {
        address: this.address.value,
        phone: this.phone.value,
        quantity: this.quantity.value,
        precaution: this.precaution.value,
        date: this.date.value,
        expiry_date: this.expiryDate.value,
        duration: this.duration.value,
        indication: this.indication.value,
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    onClickBack () {
      this.$router.push('/admin/clinic/settings')
    },
    async fetchDrugLabel () {
      try {
        this.isFetching = true
        const { data } = await this.$axios.get(`/clinics/${this.company.slug}/medication_label_settings`)
        this.mapData(data)
        this.isFetching = false
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    mapData (data) {
      for (const key in data) {
        switch (key) {
          case 'address':
            this.propertyItems[4].value = data[key]
            break

          case 'phone':
            this.propertyItems[5].value = data[key]
            break

          case 'precaution':
            this.propertyItems[6].value = data[key]
            break

          case 'quantity':
            this.propertyItems[7].value = data[key]
            break

          case 'date':
            this.propertyItems[8].value = data[key]
            break

          case 'expiry_date':
            this.propertyItems[9].value = data[key]
            break

          case 'duration':
            this.propertyItems[10].value = data[key]
            break

          case 'indication':
            this.propertyItems[11].value = data[key]
            break

          default:
            break
        }
      }
    },
    async onSave () {
      try {
        this.isLoading = true
        await this.$axios.patch(`/clinics/${this.company.slug}/medication_label_settings`, this.payload)
        this.showMessage(['success', this.$t('success_message.update_successful')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoading = false
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.chip {
  height: 25px;
}
</style>